import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { Close as CloseIcon, Search, ArrowBack } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { gql } from '@apollo/client';
import { DateTime } from 'luxon';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';

const GET_ACTIVITIES_FOR_CHAT = gql`
  query GetActivitiesForRosterCheckinChat {
    vm {
      programs {
        programId
        name
        activities(includeInactiveActivities: false) {
          activityId
          name
          program {
            programId
            name
          }
        }
      }
    }
  }
`;

interface Activity {
  activityId: string;
  name: string;
  program: { programId: string; name: string } | null;
}

interface ShareRosterCheckinDialogProps {
  open: boolean;
  type: 'roster_link' | 'checkin_link' | 'cico_link' | 'report_share';
  onClose: () => void;
  onSubmit: (content: string, metadata: Record<string, unknown>) => void;
}

// eslint-disable-next-line @typescript-eslint/camelcase, camelcase
const typeLabels: Record<
  string,
  {
    title: string;
    subtitle: string;
    dateSubtitle: string;
    submitLabel: string;
    contentLabel: string;
    emptyLabel: string;
  }
> = {
  // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
  roster_link: {
    title: 'Share Roster',
    subtitle: 'Select an activity to share its roster',
    dateSubtitle: 'Select an upcoming date',
    submitLabel: 'Share Roster',
    contentLabel: 'Shared a roster',
    emptyLabel: 'No upcoming dates found',
  },
  // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
  checkin_link: {
    title: 'Share Check-in Link',
    subtitle: 'Select an activity to share',
    dateSubtitle: 'Select an upcoming date',
    submitLabel: 'Share Check-in',
    contentLabel: 'Shared a check-in link',
    emptyLabel: 'No upcoming dates found',
  },
  // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
  cico_link: {
    title: 'Share Check In',
    subtitle: 'Select an activity to share',
    dateSubtitle: 'Select an upcoming date',
    submitLabel: 'Share Check In',
    contentLabel: 'Shared a check-in',
    emptyLabel: 'No upcoming dates found',
  },
  // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
  report_share: {
    title: 'Share Report',
    subtitle: 'Select an activity to share its report',
    dateSubtitle: 'Select a past date',
    submitLabel: 'Share Report',
    contentLabel: 'Shared a report',
    emptyLabel: 'No past dates found for this activity',
  },
};

export const ShareRosterCheckinDialog = ({ open, type, onClose, onSubmit }: ShareRosterCheckinDialogProps) => {
  const apiUrl = useApiUrl();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);

  const [runQuery, { data, loading }] = useCampfireLazyQuery<any, any>(GET_ACTIVITIES_FOR_CHAT);

  const sidebarFetch = useCampfireFetch<any>({ defer: true });

  useEffect(() => {
    if (open) {
      runQuery();
      setSelectedActivity(null);
      setSelectedDate(null);
      setSearchTerm('');
      setAvailableDates([]);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedActivity) return;

    setLoadingDates(true);
    const now = DateTime.local();
    const isHistorical = type === 'report_share';

    const startDate = isHistorical ? now.minus({ months: 6 }).toISODate() : now.minus({ days: 1 }).toISODate();
    const endDate = isHistorical ? now.plus({ days: 1 }).toISODate() : now.plus({ months: 3 }).toISODate();

    sidebarFetch
      .run({
        url: `${apiUrl}/vm/activity/roster/sidebar-data`,
        method: 'POST',
        data: {
          activityIds: [selectedActivity.activityId],
          startDate,
          endDate,
        },
      })
      .then((res: any) => {
        const activities = res?.data?.data?.activities || [];
        if (activities.length > 0) {
          const activityData = activities[0];
          const dates: string[] = [...(activityData.occurrencesBetween || [])];

          Object.keys(activityData.rosterTemplates || {}).forEach((d: string) => {
            if (!dates.includes(d)) dates.push(d);
          });

          const today = now.toISODate();
          if (isHistorical) {
            const pastDates = dates.filter((d) => d <= today!);
            pastDates.sort((a, b) => b.localeCompare(a));
            setAvailableDates(pastDates);
          } else {
            dates.sort();
            setAvailableDates(dates);
          }
        }
        setLoadingDates(false);
      })
      .catch(() => {
        setLoadingDates(false);
      });
  }, [selectedActivity]);

  const allActivities: Activity[] = (data?.vm?.programs || []).flatMap((p: any) => p.activities || []);

  const filtered = searchTerm.trim()
    ? allActivities.filter((a) => a.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : allActivities;

  const handleSubmit = () => {
    if (!selectedActivity || !selectedDate) return;

    const labels = typeLabels[type] || typeLabels.roster_link;
    onSubmit(labels.contentLabel, {
      type,
      activityId: selectedActivity.activityId,
      activityDate: selectedDate,
    });
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedActivity(null);
    setSelectedDate(null);
    setAvailableDates([]);
    onClose();
  };

  const labels = typeLabels[type] || typeLabels.roster_link;

  const formatDate = (dateStr: string) => {
    const dt = DateTime.fromISO(dateStr);
    const now = DateTime.local();
    const today = now.toISODate();
    const tomorrow = now.plus({ days: 1 }).toISODate();
    const yesterday = now.minus({ days: 1 }).toISODate();

    let relative = '';
    if (dateStr === today) relative = ' — Today';
    else if (dateStr === tomorrow) relative = ' — Tomorrow';
    else if (dateStr === yesterday) relative = ' — Yesterday';

    return `${dt.toFormat('cccc d LLLL, y')}${relative}`;
  };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <Typography style={{ fontWeight: 700, fontSize: '1.15rem', color: '#333' }}>{labels.title}</Typography>
            <Typography variant='body2' style={{ color: '#999', marginTop: 2 }}>
              {selectedActivity ? labels.dateSubtitle : labels.subtitle}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton aria-label='Close' size='small' onClick={handleClose} style={{ marginTop: -4 }}>
              <CloseIcon style={{ fontSize: 20, color: '#999' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16, paddingBottom: 8 }}>
        {!selectedActivity ? (
          <>
            <TextField
              fullWidth
              variant='outlined'
              placeholder='Search activities...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: 16 }}
              InputProps={{
                style: { borderRadius: 10, fontSize: '0.9rem' },
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search style={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            {loading && (
              <Box display='flex' justifyContent='center' p={3}>
                <CircularProgress size={24} />
              </Box>
            )}
            <List style={{ maxHeight: 350, overflow: 'auto' }} disablePadding>
              {filtered.map((activity) => (
                <ListItem
                  key={activity.activityId}
                  button
                  onClick={() => setSelectedActivity(activity)}
                  style={{ borderRadius: 8, marginBottom: 2, paddingTop: 8, paddingBottom: 8 }}
                >
                  <ListItemText
                    primary={activity.name}
                    secondary={activity.program?.name}
                    primaryTypographyProps={{ style: { fontWeight: 500, fontSize: '0.9rem', color: '#333' } }}
                    secondaryTypographyProps={{ style: { fontSize: '0.8rem' } }}
                  />
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#ccc'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M9 6l6 6l-6 6' />
                  </svg>
                </ListItem>
              ))}
              {!loading && filtered.length === 0 && (
                <Box p={3} textAlign='center'>
                  <Typography variant='body2' style={{ color: '#999' }}>
                    No activities found
                  </Typography>
                </Box>
              )}
            </List>
          </>
        ) : (
          <>
            <Box display='flex' alignItems='center' mb={2}>
              <IconButton
                size='small'
                onClick={() => {
                  setSelectedActivity(null);
                  setSelectedDate(null);
                  setAvailableDates([]);
                }}
                style={{ marginRight: 8 }}
              >
                <ArrowBack style={{ fontSize: 20 }} />
              </IconButton>
              <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
                {selectedActivity.name}
              </Typography>
            </Box>

            {loadingDates && (
              <Box display='flex' justifyContent='center' p={3}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!loadingDates && availableDates.length === 0 && (
              <Box p={3} textAlign='center'>
                <Typography variant='body2' style={{ color: '#999' }}>
                  {labels.emptyLabel}
                </Typography>
              </Box>
            )}

            {!loadingDates && availableDates.length > 0 && (
              <List style={{ maxHeight: 350, overflow: 'auto' }} disablePadding>
                {availableDates.map((date) => (
                  <ListItem
                    key={date}
                    button
                    selected={selectedDate === date}
                    onClick={() => setSelectedDate(date)}
                    style={{ borderRadius: 8, marginBottom: 2, paddingTop: 8, paddingBottom: 8 }}
                  >
                    <ListItemText
                      primary={formatDate(date)}
                      primaryTypographyProps={{
                        style: { fontSize: '0.9rem', fontWeight: selectedDate === date ? 600 : 400, color: '#333' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '8px 20px 16px' }}>
        <TabletButton variant='text' onClick={handleClose} style={{ marginRight: 8, color: '#999' }}>
          Cancel
        </TabletButton>
        {selectedActivity && selectedDate && (
          <TabletButton variant='contained' color='primary' onClick={handleSubmit}>
            {labels.submitLabel}
          </TabletButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
